import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { HardDrive, Loader2, ArrowRightLeft, CheckCircle2, AlertTriangle, Film, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { useAuth } from "@/lib/auth";
import R2IntegrityTool from "@/components/R2IntegrityTool";

// R2 object-storage panel: shows how many assets are served from Cloudflare R2
// vs still on legacy Cloudflare Stream, and (super-admin) lets you migrate the
// originals into R2 with a live progress bar.
export default function R2StoragePanel() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showIntegrity, setShowIntegrity] = useState(false);
  const [demotedCount, setDemotedCount] = useState(0);
  const pollRef = useRef(null);

  const fetchStatus = async () => {
    try {
      const r = await api.get("/r2/status");
      setStatus(r.data);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    fetchStatus();
    (async () => {
      try {
        const r = await api.post("/assets/demoted");
        const n = r.data?.demoted_count || 0;
        setDemotedCount(n);
        if (n > 0) setShowIntegrity(true); // surface broken assets immediately
      } catch { /* non-blocking */ }
    })();
    return () => pollRef.current && clearInterval(pollRef.current);
  }, []);

  // Poll while a migration OR an HLS transcode batch is running.
  useEffect(() => {
    const running = status?.migration?.running || (status?.assets_hls_pending || 0) > 0;
    if (running && !pollRef.current) {
      pollRef.current = setInterval(fetchStatus, 3000);
    } else if (!running && pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, [status?.migration?.running, status?.assets_hls_pending]);

  const migrate = async () => {
    const n = status?.assets_migratable || 0;
    if (!n) { toast.info("Nothing to migrate — all assets are already on R2"); return; }
    if (!window.confirm(`Migrate ${n} asset${n === 1 ? "" : "s"} from object storage into R2? This runs in the background.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/r2/migrate", { dry_run: false });
      toast.success(`Migration started · ${r.data.will_migrate} asset(s) queued`);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Migration failed to start");
    }
    setBusy(false);
  };

  const transcodeAll = async () => {
    const n = status?.assets_hls_todo || 0;
    if (!n) { toast.info("All R2 assets already have an adaptive HLS ladder"); return; }
    if (!window.confirm(`Transcode ${n} asset${n === 1 ? "" : "s"} into adaptive HLS (ABR)? This is CPU-intensive and runs in the background.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/r2/transcode-all", { dry_run: false });
      toast.success(`HLS transcoding started · ${r.data.will_transcode} asset(s) queued`);
      await fetchStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transcode failed to start");
    }
    setBusy(false);
  };

  if (!status) return null;

  const mig = status.migration;
  const running = mig?.running;
  const total = mig?.total || 0;
  const done = (mig?.done || 0) + (mig?.failed || 0);
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;

  return (
    <div
      data-testid="r2-storage-panel"
      className="mb-4 rounded-sm border border-cyan-500/30 bg-cyan-500/5 backdrop-blur-md p-4"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <HardDrive className="w-4 h-4 text-cyan-400" />
          <div className="font-display font-bold text-sm">Cloudflare R2 storage</div>
          {status.enabled ? (
            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-emerald-500/15 text-emerald-600 dark:text-emerald-300 border border-emerald-400/40">
              active · {status.bucket}
            </span>
          ) : (
            <span className="text-[9px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-muted/40 text-muted-foreground border border-border">
              not configured
            </span>
          )}
        </div>
        {isSuper && status.enabled && (
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              onClick={transcodeAll}
              disabled={busy || (status.assets_hls_pending || 0) > 0 || !status.assets_hls_todo}
              data-testid="r2-transcode-all-btn"
              className="rounded-sm h-8 text-xs border border-fuchsia-400/40 bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300 hover:bg-fuchsia-500/20"
              variant="outline"
            >
              {(status.assets_hls_pending || 0) > 0
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <Film className="w-3.5 h-3.5 mr-1.5" />}
              {(status.assets_hls_pending || 0) > 0
                ? `Transcoding ${status.assets_hls_pending}…`
                : `Generate HLS (${status.assets_hls_todo || 0})`}
            </Button>
            <Button
              size="sm"
              onClick={migrate}
              disabled={busy || running || !status.assets_migratable}
              data-testid="r2-migrate-btn"
              className="rounded-sm h-8 text-xs tenant-btn"
            >
              {busy || running
                ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                : <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />}
              {running ? "Migrating…" : `Migrate ${status.assets_migratable} to R2`}
            </Button>
          </div>
        )}
      </div>

      <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-2">
        <Stat label="On R2" value={status.assets_on_r2} testId="r2-stat-on-r2" tone="emerald" icon={<CheckCircle2 className="w-3 h-3" />} />
        <Stat label="Adaptive HLS" value={status.assets_on_hls} testId="r2-stat-on-hls" tone="fuchsia" icon={<Film className="w-3 h-3" />} />
        <Stat label="On Stream only" value={status.assets_on_stream_only} testId="r2-stat-on-stream" tone="cyan" />
        <Stat label="Migratable" value={status.assets_migratable} testId="r2-stat-migratable" tone="cyan" />
        <Stat label="Missing source" value={status.assets_missing_source} testId="r2-stat-missing" tone="amber" icon={status.assets_missing_source ? <AlertTriangle className="w-3 h-3" /> : null} />
      </div>

      {mig && (running || total > 0) && (
        <div className="mt-3" data-testid="r2-migration-progress">
          <div className="flex items-center justify-between text-[10px] font-mono text-muted-foreground mb-1">
            <span>{running ? "Migrating…" : "Last migration batch"}</span>
            <span>{done}/{total} · {mig.failed || 0} failed</span>
          </div>
          <div className="w-full h-1.5 bg-accent rounded-full overflow-hidden">
            <div className="h-full bg-cyan-400 transition-all" style={{ width: `${pct}%` }} />
          </div>
          {mig.last_error && !running && (
            <div className="text-[10px] font-mono text-rose-400 mt-1 truncate">Last error: {mig.last_error}</div>
          )}
        </div>
      )}

      {status.enabled && (
        <div className="mt-4 pt-3 border-t border-border">
          <button
            onClick={() => setShowIntegrity((s) => !s)}
            data-testid="assets-r2-integrity-toggle"
            className="flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-cyan-600 dark:text-cyan-300 hover:opacity-80"
          >
            <ShieldCheck className="w-3.5 h-3.5" />
            Validate R2 integrity
            {demotedCount > 0 && (
              <span className="px-1.5 py-0.5 rounded-sm bg-red-500/15 text-red-400 border border-red-500/40 text-[10px]" data-testid="assets-r2-demoted-count">
                {demotedCount} demoted
              </span>
            )}
            {showIntegrity ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          </button>
          {showIntegrity && (
            <div className="mt-3" data-testid="assets-r2-integrity-body">
              <R2IntegrityTool />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, testId, tone = "cyan", icon }) {
  const toneMap = {
    emerald: "text-emerald-600 dark:text-emerald-300",
    cyan: "text-cyan-600 dark:text-cyan-300",
    fuchsia: "text-fuchsia-600 dark:text-fuchsia-300",
    amber: "text-amber-600 dark:text-amber-400",
  };
  return (
    <div className="rounded-sm border border-border bg-black/20 px-3 py-2" data-testid={testId}>
      <div className={`text-lg font-display font-black flex items-center gap-1.5 ${toneMap[tone]}`}>
        {icon}{value ?? 0}
      </div>
      <div className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">{label}</div>
    </div>
  );
}
